import { memo, useState } from "react";
import type { ActiveInspectionRegister } from "../../InspectionsLists.d";
import { useDate } from "@/hooks/use-date/useDate";
import circleArrow from "@/assets/images/inspections-table/circleArrow.svg";
import { Link } from "react-router-dom";
import { BASE_URL } from "@/assets/constants/constants";
import PhasePill from "../../phase-pill/PhasePill";
import downloaDocument from "@/assets/images/inspections-table/downloadDocument.svg";
import {
  deleteInspectionId,
  getInspectionPDF,
} from "@/lib/services/inspections";
import { useLoader } from "@/context/loader-context/LoaderContext";
import { appToast } from "@/lib/toast";
import { useTranslate } from "@/hooks/use-translate/useTranslate";
import { Icon } from "@/components/icons/Icon";
import { useUserStore } from "@/store/useUser";
import ModalRemoveInspection from "@/views/inspections/components/modal-remove-inspection/ModalRemoveInspection";

export interface ActivedInspectionsProps {
  tableData: ActiveInspectionRegister[];
  getNoSortedInspections: () => void;
}

const ActivedInspections = ({
  tableData,
  getNoSortedInspections,
}: ActivedInspectionsProps) => {
  const { formatDate, getDaysSince } = useDate();
  const { showLoader, hideLoader } = useLoader();
  const translate = useTranslate();
  const { isSupervisor } = useUserStore();

  const [inspectionToRemove, setInspectionToRemove] =
    useState<ActiveInspectionRegister | null>(null);

  const downloadPdf = async (id: number) => {
    try {
      showLoader();
      const response = await getInspectionPDF({ id });

      const url = window.URL.createObjectURL(
        new Blob([response], { type: "application/pdf" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `inspection_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      appToast.success(translate("success"), translate("pdfDownloadSuccess"));
    } catch (error) {
      const e = error as Error;
      appToast.error(translate("error"), e.message);
    } finally {
      hideLoader();
    }
  };

  const removeInspection = async (id: number) => {
    try {
      showLoader();
      await deleteInspectionId({ id });
      appToast.success(translate("success"), translate("inspectionRemoved"));
      getNoSortedInspections();
      setInspectionToRemove(null);
    } catch (error) {
      const e = error as Error;
      appToast.error(translate("error"), e.message);
    } finally {
      hideLoader();
    }
  };

  return tableData?.map((register: ActiveInspectionRegister, index: number) => (
    <>
      <tr
        data-testid="active-inspection-row"
        key={`tr-${index}`}
        className={`${index % 2 === 0 ? "table-odd-row" : "table-even-row"}${
          index === tableData.length - 1 ? " last-row" : ""
        } h-16 border-b border-b-ib-gray-lightest text-[14px] leading-[16px] text-ib-gray-darkest tracking-neg-1`}
      >
        <td className="h-full left-cell"></td>
        <td className="h-16 p-3 flex flex-row justify-center gap-x-2 items-center">
          <span>{formatDate(register.createdAt)}</span>
          <span className="font-ib-bold">
            {getDaysSince(register.createdAt)}
          </span>
        </td>
        <td className="p-3 text-center">{register.engineName}</td>
        <td className="p-3 text-center">{register.snStand}</td>
        <td className="p-3 text-center">{register.esnIn}</td>
        <td className="p-3 text-center">{register.colour}</td>
        <td className="px-3 py-2">
          <div className="w- full flex flex-row justify-start gap-x-2 items-center">
            <span>{formatDate(register.phase?.date)}</span>
            <PhasePill phaseName={register.phase.name} />
          </div>
        </td>
        <td className="px-3 py-2 right-cell">
          <div className="w- full flex flex-row justify-end items-center gap-x-6">
            {isSupervisor ? (
              <button
                data-testid="trash-button"
                value={register.id}
                onClick={() => setInspectionToRemove(register)}
                onTouchStart={() => setInspectionToRemove(register)}
              >
                <Icon name="trash" className="text-ib-red-main size-6" />
              </button>
            ) : null}
            <button
              data-testid="pdf-button"
              value={register.id}
              onClick={() => downloadPdf(register.id)}
              onTouchStart={() => downloadPdf(register.id)}
            >
              <img className="h-[20px] min-w-[20px]" src={downloaDocument} />
            </button>
            <Link to={`${BASE_URL}/${register.id}`}>
              <button data-testid="open-inspection-button" value={register.id}>
                <img className="h-[20px] min-w-[20px]" src={circleArrow} />
              </button>
            </Link>
          </div>
          {inspectionToRemove && inspectionToRemove.id === register.id ? (
            <ModalRemoveInspection
              inspection={inspectionToRemove}
              closeModal={() => setInspectionToRemove(null)}
              removeInspection={removeInspection}
            />
          ) : null}
        </td>
      </tr>
    </>
  ));
};

export default memo(ActivedInspections);
